import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AuditOpinionQualifiedOption } from '../../../models/auditOpinionQualifiedOption';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-audit-opinion-qualified-option-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './audit-opinion-qualified-option-list.component.html',
  styleUrl: './audit-opinion-qualified-option-list.component.scss'
})
export class AuditOpinionQualifiedOptionListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  auditOpinionQualifiedOptions = signal<AuditOpinionQualifiedOption[]>([]);
  rightItem: AuditOpinionQualifiedOption | undefined = undefined;
  matchedItems: AuditOpinionQualifiedOption[] | undefined = undefined;

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadAuditOpinionQualifiedOptions();
  }

  loadAuditOpinionQualifiedOptions(): void {
    this.spinnerService.show();
    this.lutService.getAuditOpinionQualifiedOptions().subscribe({
      next: (data) => {
        // Sort alphabetically by name before setting
        const sortedData = this.sortAuditOpinionQualifiedOptions(data);
        this.auditOpinionQualifiedOptions.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading audit opinion qualified options:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortAuditOpinionQualifiedOptions(auditOpinionQualifiedOptions: AuditOpinionQualifiedOption[]): AuditOpinionQualifiedOption[] {
    return [...auditOpinionQualifiedOptions].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateAuditOpinionQualifiedOption(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(auditOpinionQualifiedOption: AuditOpinionQualifiedOption): void {
  }

  onEditAuditOpinionQualifiedOption(auditOpinionQualifiedOption: AuditOpinionQualifiedOption): void {
  }

  onDeleteAuditOpinionQualifiedOption(auditOpinionQualifiedOption: AuditOpinionQualifiedOption): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(auditOpinionQualifiedOption.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalAuditOpinionQualifiedOptions = this.auditOpinionQualifiedOptions();

          // 1. Optimistically remove from local state immediately
          this.auditOpinionQualifiedOptions.update(auditOpinionQualifiedOptions => auditOpinionQualifiedOptions.filter(aoqo => aoqo.id !== auditOpinionQualifiedOption.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteAuditOpinionQualifiedOption(auditOpinionQualifiedOption.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Audit opinion qualified option deleted successfully');
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting audit opinion qualified option:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.auditOpinionQualifiedOptions.set(originalAuditOpinionQualifiedOptions);
              this.toast.error(`<strong>Failed to delete Audit Opinion Qualified Option:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: AuditOpinionQualifiedOption, newName: string} | {item: AuditOpinionQualifiedOption, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: AuditOpinionQualifiedOption;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.auditOpinionQualifiedOptions.update(auditOpinionQualifiedOptions => {
      const updated = auditOpinionQualifiedOptions.map(aoqo => aoqo.id === event.item.id ? updatedItem : aoqo);
      return this.sortAuditOpinionQualifiedOptions(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateAuditOpinionQualifiedOption(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Audit opinion qualified option updated successfully');
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.auditOpinionQualifiedOptions.update(auditOpinionQualifiedOptions => {
          const updated = auditOpinionQualifiedOptions.map(aoqo => aoqo.id === event.item.id ? response : aoqo);
          return this.sortAuditOpinionQualifiedOptions(updated);
        });
      },
      error: (error) => {
        console.error('Error updating audit opinion qualified option:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.auditOpinionQualifiedOptions.update(auditOpinionQualifiedOptions =>
          auditOpinionQualifiedOptions.map(aoqo => aoqo.id === event.item.id ? originalItem : aoqo)
        );
        this.toast.error(`<strong>Failed to update Audit Opinion Qualified Option:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newAuditOpinionQualifiedOption: AuditOpinionQualifiedOption = { id: tempId, name: event['name'], systemReserved: false };

    // 1. Optimistically add to local state immediately and sort
    this.auditOpinionQualifiedOptions.update(auditOpinionQualifiedOptions => this.sortAuditOpinionQualifiedOptions([...auditOpinionQualifiedOptions, newAuditOpinionQualifiedOption]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createAuditOpinionQualifiedOption({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Audit opinion qualified option created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.auditOpinionQualifiedOptions.update(auditOpinionQualifiedOptions => {
          const updated = auditOpinionQualifiedOptions.map(aoqo => aoqo.id === tempId ? response : aoqo);
          return this.sortAuditOpinionQualifiedOptions(updated);
        });
      },
      error: (error) => {
        console.error('Error creating audit opinion qualified option:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.auditOpinionQualifiedOptions.update(auditOpinionQualifiedOptions =>
          auditOpinionQualifiedOptions.filter(aoqo => aoqo.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Audit Opinion Qualified Option:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onReadyToMatch(event: {rightItem?: AuditOpinionQualifiedOption, matchedItems?: AuditOpinionQualifiedOption[]}): void {
    this.rightItem = event.rightItem;
    this.matchedItems = event.matchedItems;
  }

  onAuditOpinionQualifiedOptionCorrected(): void {
    // Validate that we have the required data
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select items to merge');
      return;
    }

    // Prepare the bulk merge request
    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    // Show confirmation dialog
    const itemsToMerge = this.matchedItems.map(item => item.name).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.name}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        // Store original list for rollback in case of error
        const originalAuditOpinionQualifiedOptions = this.auditOpinionQualifiedOptions();

        // 1. Optimistically remove merged items from local state
        this.auditOpinionQualifiedOptions.update(auditOpinionQualifiedOptions =>
          auditOpinionQualifiedOptions.filter(aoqo => !bulkMergeRequest.leftIds.includes(aoqo.id))
        );

        // Clear the selection state
        this.rightItem = undefined;
        this.matchedItems = undefined;

        // 2. Call backend API
        this.spinnerService.show();
        this.lutService.mergeAuditOpinionQualifiedOptionsBulk(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Audit opinion qualified options merged successfully');
            // 3. On success: Clear the lookup table's internal matching state and reload the list
            this.lookupTable.clearMatchingState();
            this.loadAuditOpinionQualifiedOptions();
          },
          error: (error) => {
            console.error('Error merging audit opinion qualified options:', error);
            this.spinnerService.hide();
            // 4. On error: Restore original list
            this.auditOpinionQualifiedOptions.set(originalAuditOpinionQualifiedOptions);
            // Restore selection state for retry
            this.rightItem = originalAuditOpinionQualifiedOptions.find(aoqo => aoqo.id === bulkMergeRequest.rightId);
            this.matchedItems = originalAuditOpinionQualifiedOptions.filter(aoqo => bulkMergeRequest.leftIds.includes(aoqo.id));
            this.toast.error(`<strong>Failed to merge Audit Opinion Qualified Options:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }
}
